import { useEffect, useState } from 'react';
import './DepartmentOrgChart.css';

export type DepartmentPerson = Record<string, unknown> & {
  key?: string;
  name?: string;
  role?: string;
  photo?: string;
  photoUrl?: string;
  image?: string;
};

export type DepartmentOrgChartData = {
  type: 'department-org-chart';
  title?: string;
  root?: {
    label?: string;
  };
  departments: Array<{
    key?: string;
    name?: string;
    center?: string;
    people: DepartmentPerson[];
  }>;
};

const getText = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value.trim() : value == null ? fallback : String(value);

const ignoredPersonFields = new Set(['key', 'name', 'role', 'photo', 'photoUrl', 'image']);

const personFieldLabels: Record<string, string> = {
  department: 'Departamento',
  center: 'Centro',
  email: 'Email',
  site: 'Sede',
  location: 'Ubicación',
  building: 'Edificio',
  province: 'Provincia',
};

const formatFieldLabel = (field: string): string =>
  personFieldLabels[field] ||
  field
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const displayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const getPhotoValue = (person: DepartmentPerson): string | null => {
  const photo = [person.photo, person.photoUrl, person.image].find(
    (value) => typeof value === 'string' && value.trim(),
  );

  return typeof photo === 'string' ? photo.trim() : null;
};

const getPhotoSrc = (
  person: DepartmentPerson,
  placeholderPhotos: Set<string>,
): string | null => {
  const photo = getPhotoValue(person);

  if (!photo || placeholderPhotos.has(photo)) {
    return null;
  }

  const normalizedPhoto = photo
    .replace(/^data:image\/[^;]+;base64,/i, '')
    .replace(/\s/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(normalizedPhoto)) {
    return photo.trim().startsWith('http') ? photo.trim() : null;
  }

  try {
    const decoded = atob(normalizedPhoto);
    const bytes = Uint8Array.from(decoded.slice(0, 12), (character) => character.charCodeAt(0));
    let mimeType = 'image/jpeg';

    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      mimeType = 'image/png';
    } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      mimeType = 'image/gif';
    } else if (
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      mimeType = 'image/webp';
    }

    return `data:${mimeType};base64,${normalizedPhoto}`;
  } catch {
    return null;
  }
};

const PersonCard = ({
  person,
  placeholderPhotos,
  onSelect,
}: {
  person: DepartmentPerson;
  placeholderPhotos: Set<string>;
  onSelect: (person: DepartmentPerson) => void;
}) => {
  const name = getText(person.name, 'Persona sin nombre');
  const photo = getPhotoSrc(person, placeholderPhotos);

  return (
    <article
      className="department-person-card"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(person)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(person);
        }
      }}
      aria-label={`Ver información de ${name}`}
    >
      <div className="department-person-card__avatar">
        {photo ? (
          <img src={photo} alt={`Foto de ${name}`} />
        ) : (
          <span aria-hidden="true">{getInitials(name)}</span>
        )}
      </div>
      <div className="department-person-card__identity">
        <h3>{name}</h3>
        <p>{getText(person.role, 'Cargo no indicado')}</p>
      </div>
    </article>
  );
};

const PersonDetailsModal = ({
  person,
  placeholderPhotos,
  onClose,
}: {
  person: DepartmentPerson;
  placeholderPhotos: Set<string>;
  onClose: () => void;
}) => {
  const name = getText(person.name, 'Persona sin nombre');
  const photo = getPhotoSrc(person, placeholderPhotos);
  const fields = Object.entries(person).filter(([field, value]) =>
    !ignoredPersonFields.has(field) && displayValue(value).trim().length > 0,
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="department-person-modal" role="presentation" onClick={onClose}>
      <div
        className="department-person-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="department-person-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="department-person-modal__close"
          aria-label="Cerrar información de la persona"
          onClick={onClose}
        >
          ×
        </button>
        <header className="department-person-modal__header">
          <div className="department-person-modal__avatar">
            {photo ? (
              <img src={photo} alt={`Foto de ${name}`} />
            ) : (
              <span aria-hidden="true">{getInitials(name)}</span>
            )}
          </div>
          <div>
            <h2 id="department-person-modal-title">{name}</h2>
            {person.role && <p>{getText(person.role)}</p>}
          </div>
        </header>
        <div className="department-person-modal__details">
          {fields.map(([field, value]) => {
            const renderedValue = displayValue(value);
            return (
              <div className="department-person-modal__field" key={field}>
                <span>{formatFieldLabel(field)}</span>
                {field === 'email' ? (
                  <a href={`mailto:${renderedValue}`}>{renderedValue}</a>
                ) : (
                  <strong>{renderedValue}</strong>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const DepartmentOrgChart = ({ data }: { data: DepartmentOrgChartData }) => {
  const [selectedPerson, setSelectedPerson] = useState<DepartmentPerson | null>(null);
  const photoCounts = new Map<string, number>();

  data.departments.forEach((department) => {
    department.people.forEach((person) => {
      const photo = getPhotoValue(person);
      if (photo) {
        photoCounts.set(photo, (photoCounts.get(photo) || 0) + 1);
      }
    });
  });

  const placeholderPhotos = new Set(
    Array.from(photoCounts.entries())
      .filter(([, count]) => count > 1)
      .map(([photo]) => photo),
  );

  return (
    <section className="department-org-chart">
      {data.title && <h2 className="department-org-chart__title">{data.title}</h2>}
      {data.root?.label && <p className="department-org-chart__root">{data.root.label}</p>}

      <div className="department-org-chart__departments">
        {data.departments.map((department, departmentIndex) => (
          <section
            className="department-org-chart__department"
            key={department.key || `${department.name || 'department'}-${departmentIndex}`}
          >
            <header className="department-org-chart__department-header">
              <h3>{getText(department.name, 'Departamento sin nombre')}</h3>
              {department.center && <p>{department.center}</p>}
            </header>
            <div className="department-org-chart__people">
              {department.people.map((person, personIndex) => (
                <PersonCard
                  key={person.key || `${getText(person.name)}-${personIndex}`}
                  person={person}
                  placeholderPhotos={placeholderPhotos}
                  onSelect={setSelectedPerson}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
      {selectedPerson && (
        <PersonDetailsModal
          person={selectedPerson}
          placeholderPhotos={placeholderPhotos}
          onClose={() => setSelectedPerson(null)}
        />
      )}
    </section>
  );
};

export default DepartmentOrgChart;
